import React from 'react';
import { NavLink } from 'react-router-dom';
import { ArrowLeft, Eye, Database, UserCheck, Trash2, Mail, FileText } from 'lucide-react';

const Privacy: React.FC = () => {
    return (
        <div className="min-h-screen bg-background-dark text-white font-sans selection:bg-primary/30">
            {/* Navigation */}
            <nav className="fixed top-0 w-full z-50 bg-background-dark/80 backdrop-blur-md border-b border-white/5">
                <div className="max-w-7xl mx-auto px-4 h-20 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <NavLink to="/" className="text-slate-400 hover:text-white transition-colors flex items-center gap-2">
                            <ArrowLeft className="w-4 h-4" /> Volver
                        </NavLink>
                        <div className="h-6 w-px bg-white/10"></div>
                        <img src="/logo.png" alt="FlowSpec QA" className="h-8 w-auto opacity-80" />
                    </div>
                </div>
            </nav>

            {/* Hero */}
            <section className="pt-40 pb-20 px-4">
                <div className="max-w-4xl mx-auto text-center">
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-primary text-xs font-bold mb-6 tracking-widest uppercase">
                        <Eye className="w-4 h-4" />
                        Política de Privacidad
                    </div>
                    <h1 className="text-4xl md:text-6xl font-black mb-8 tracking-tight">
                        Tu privacidad es{' '}
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-indigo-400">
                            nuestra responsabilidad.
                        </span>
                    </h1>
                    <p className="text-lg text-slate-400 leading-relaxed max-w-2xl mx-auto">
                        En FlowSpec QA tratamos tus datos con el mismo rigor con el que diseñamos la plataforma: con criterio, transparencia y trazabilidad.
                        Última actualización: <span className="text-slate-300 font-semibold">Abril 2026</span>.
                    </p>
                </div>
            </section>

            {/* Pillars */}
            <section className="py-20 px-4 border-y border-white/5 bg-white/[0.02]">
                <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-12">
                    {[
                        {
                            icon: <Database className="w-8 h-8 text-primary" />,
                            title: "Datos que recopilamos",
                            desc: "Solo recopilamos lo estrictamente necesario: nombre, correo electrónico, empresa y rol profesional al registrarte. No recopilamos datos sensibles personales, financieros ni de identificación oficial."
                        },
                        {
                            icon: <Eye className="w-8 h-8 text-indigo-400" />,
                            title: "Cómo usamos tus datos",
                            desc: "Tus datos se usan exclusivamente para operar la plataforma, enviarte comunicaciones relacionadas con el servicio y mejorar la experiencia. Nunca los vendemos ni compartimos con terceros con fines comerciales."
                        },
                        {
                            icon: <UserCheck className="w-8 h-8 text-emerald-400" />,
                            title: "Tus derechos",
                            desc: "Tienes derecho a acceder, rectificar y eliminar tus datos en cualquier momento. Puedes solicitar una copia de tu información o la eliminación completa de tu cuenta escribiendo a contacto@flowspecqa.com."
                        }
                    ].map((item, i) => (
                        <div key={i} className="p-8 rounded-3xl bg-background-dark border border-white/5 hover:border-white/10 transition-colors">
                            <div className="mb-6 bg-white/5 w-16 h-16 rounded-2xl flex items-center justify-center">
                                {item.icon}
                            </div>
                            <h3 className="text-xl font-bold mb-4">{item.title}</h3>
                            <p className="text-slate-400 leading-relaxed text-sm">{item.desc}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* Detail Sections */}
            <section className="py-20 px-4">
                <div className="max-w-5xl mx-auto">
                    <h2 className="text-3xl font-bold mb-12">Detalles de la Política</h2>

                    <div className="space-y-8">
                        <div className="flex gap-6 items-start p-6 rounded-2xl bg-white/5 border border-white/5">
                            <div className="shrink-0 mt-1"><FileText className="w-6 h-6 text-primary" /></div>
                            <div>
                                <h3 className="text-lg font-bold mb-2">Documentos e Información de Pruebas</h3>
                                <p className="text-slate-400 text-sm leading-relaxed">
                                    Los documentos que subes (Jira exports, PDFs, historias de usuario) se procesan de forma privada mediante nuestra arquitectura RAG.
                                    Tu contenido <strong className="text-white">nunca se utiliza para entrenar modelos de IA</strong> de ningún proveedor (OpenAI, Anthropic u otros).
                                    Los archivos se almacenan cifrados y asociados exclusivamente a tu organización.
                                </p>
                            </div>
                        </div>

                        <div className="flex gap-6 items-start p-6 rounded-2xl bg-white/5 border border-white/5">
                            <div className="shrink-0 mt-1"><Database className="w-6 h-6 text-primary" /></div>
                            <div>
                                <h3 className="text-lg font-bold mb-2">Retención y Eliminación de Datos</h3>
                                <p className="text-slate-400 text-sm leading-relaxed">
                                    Conservamos tus datos mientras tu cuenta esté activa. Si decides cerrar tu cuenta, eliminamos toda tu información
                                    en un plazo máximo de <strong className="text-white">30 días</strong>, salvo que la ley nos exija conservarlos por un período mayor.
                                    Los backups se purgan en ciclos de 90 días.
                                </p>
                            </div>
                        </div>

                        <div className="flex gap-6 items-start p-6 rounded-2xl bg-white/5 border border-white/5">
                            <div className="shrink-0 mt-1"><Eye className="w-6 h-6 text-primary" /></div>
                            <div>
                                <h3 className="text-lg font-bold mb-2">Cookies y Rastreo</h3>
                                <p className="text-slate-400 text-sm leading-relaxed">
                                    Utilizamos únicamente cookies técnicas esenciales para el funcionamiento de la sesión. No usamos cookies de rastreo publicitario,
                                    ni integramos servicios de análisis de comportamiento de terceros (como Google Analytics) que puedan comprometer tu privacidad.
                                </p>
                            </div>
                        </div>

                        <div className="flex gap-6 items-start p-6 rounded-2xl bg-white/5 border border-white/5">
                            <div className="shrink-0 mt-1"><Trash2 className="w-6 h-6 text-primary" /></div>
                            <div>
                                <h3 className="text-lg font-bold mb-2">Terceros y Sub-procesadores</h3>
                                <p className="text-slate-400 text-sm leading-relaxed">
                                    Para operar el servicio usamos proveedores de infraestructura de confianza (servidores en la nube, modelos de IA bajo acuerdos de confidencialidad).
                                    Todos ellos están sujetos a acuerdos de procesamiento de datos que prohíben el uso de tu información para cualquier fin distinto a la prestación del servicio.
                                </p>
                            </div>
                        </div>

                        <div className="flex gap-6 items-start p-6 rounded-2xl bg-white/5 border border-white/5">
                            <div className="shrink-0 mt-1"><Mail className="w-6 h-6 text-primary" /></div>
                            <div>
                                <h3 className="text-lg font-bold mb-2">Contacto y Solicitudes</h3>
                                <p className="text-slate-400 text-sm leading-relaxed">
                                    Para cualquier consulta, ejercicio de derechos o reporte relacionado con tu privacidad, escríbenos a{' '}
                                    <a href="mailto:contacto@flowspecqa.com" className="text-primary hover:underline">
                                        contacto@flowspecqa.com
                                    </a>.
                                    Respondemos en un plazo máximo de <strong className="text-white">72 horas hábiles</strong>.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="py-12 text-center text-slate-600 text-sm border-t border-white/5">
                <p>© 2026 IAFlows. FlowSpec QA. Todos los derechos reservados.</p>
            </footer>
        </div>
    );
};

export default Privacy;
